import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Organization {
  id: string;
  name: string;
  type: string;
  description?: string;
  created_at: string;
}

interface UserOrganization {
  organization_id: string;
  organization_name: string;
  organization_type: 'carrier' | 'broker' | 'customer' | 'logistics_provider';
  user_role: 'owner' | 'admin' | 'member';
  joined_at: string;
}

interface OrganizationContextType {
  organizations: UserOrganization[];
  currentOrganization: UserOrganization | null;
  currentOrganizationDetails: Organization | null;
  loading: boolean;
  setCurrentOrganization: (org: UserOrganization | null) => void;
  refreshOrganizations: () => Promise<void>;
  hasOrganization: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<UserOrganization | null>(null);
  const [currentOrganizationDetails, setCurrentOrganizationDetails] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchOrganizations();
    } else if (!authLoading && !user) {
      setLoading(false);
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentOrganizationDetails(null);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (currentOrganization) {
      fetchOrganizationDetails();
    } else {
      setCurrentOrganizationDetails(null);
    }
  }, [currentOrganization]);

  const fetchOrganizations = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .rpc('get_user_organizations', { user_uuid: user.id });

      if (error) throw error;

      const userOrgs = data || [];
      setOrganizations(userOrgs);

      // Auto-select first organization if none selected
      if (userOrgs.length > 0 && !currentOrganization) {
        setCurrentOrganization(userOrgs[0]);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationDetails = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', currentOrganization.organization_id)
        .single();

      if (error) throw error;
      setCurrentOrganizationDetails(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  return (
    <OrganizationContext.Provider value={{
      organizations,
      currentOrganization,
      currentOrganizationDetails,
      loading,
      setCurrentOrganization,
      refreshOrganizations,
      hasOrganization: organizations.length > 0
    }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};